import { ReactNode, useCallback, createContext, useContext, useId } from 'react'
import type { LinearTransform } from '@st/geom'
import type { DocumentViewerSend } from './document-viewer'
import { LinearTransformProvider } from './use-linear-transform'

const DocumentViewportContext = createContext<number[] | undefined>(undefined)

export type DocumentViewportProps = {
  send: DocumentViewerSend
  status: 'loading' | 'loaded' | 'failed'
  showPages: boolean
  pagesToRender: number[]
  pageTransform: LinearTransform
  children: ReactNode
  floatingLayer?: ReactNode
  ariaLabel?: string
  loadingStatusLabel?: string
}

export function DocumentViewport({
  send,
  status,
  showPages,
  pagesToRender,
  pageTransform,
  children,
  floatingLayer,
  ariaLabel,
  loadingStatusLabel
}: DocumentViewportProps) {
  const reactViewportId = useId()
  const viewportContainerId = `dvk-viewport-${reactViewportId.replace(/:/g, '')}`

  const bindViewportContainer = useCallback(
    (container: HTMLDivElement | null) => {
      send({
        type: 'viewportContainerChanged',
        containerId: container ? viewportContainerId : null
      })
    },
    [send, viewportContainerId]
  )

  const shouldRenderPages = status == 'loaded' && showPages

  return (
    <LinearTransformProvider transform={pageTransform}>
      <DocumentViewportContext.Provider value={pagesToRender}>
        <div className="relative h-full w-full overflow-hidden">
          <div
            id={viewportContainerId}
            className="h-full w-full bg-stone-500"
            ref={bindViewportContainer}
            role={ariaLabel ? 'region' : undefined}
            aria-label={ariaLabel}
            aria-busy={status == 'loading' ? true : undefined}
            style={{ overflow: 'overlay' }}
          >
            {status == 'loading' && loadingStatusLabel ? (
              <div role="status" aria-live="polite" className="sr-only">
                {loadingStatusLabel}
              </div>
            ) : null}
            {status == 'failed' ? (
              <div className="flex h-full items-center justify-center p-6">
                <div
                  role="alert"
                  className="rounded-lg bg-white px-6 py-5 text-center text-sm font-medium text-gray-900 shadow-sm"
                >
                  Unable to display this document.
                </div>
              </div>
            ) : null}
            <div>{shouldRenderPages ? children : null}</div>
          </div>
          {floatingLayer ? (
            <div className="pointer-events-none absolute inset-0 z-20">{floatingLayer}</div>
          ) : null}
        </div>
      </DocumentViewportContext.Provider>
    </LinearTransformProvider>
  )
}

export function usePageShouldRender(pageNumber: number) {
  const context = useContext(DocumentViewportContext)
  if (!context) {
    throw new Error('Page must be rendered inside DocumentViewport')
  }
  return context.includes(pageNumber)
}
